'use client';

import { useInView } from '@/lib/hooks';
import { cn } from '@/lib/utils';
import type { LicenseCertificate } from '@/types/content';
import Lottie, { type LottieRefCurrentProps } from 'lottie-react';
import Image from 'next/image';
import { useEffect, useRef } from 'react';

import certificateAnimation from '../../../public/animations/certificate-icon.json';

type CertificateCardProps = {
    certificate: LicenseCertificate;
    downloadLabel: string;
};

export default function CertificateCard({ certificate, downloadLabel }: CertificateCardProps) {
    const { isInView, elementRef } = useInView();
    const lottieRef = useRef<LottieRefCurrentProps>(null);

    useEffect(() => {
        if (!isInView) {
            return;
        }

        lottieRef.current?.goToAndPlay(0, true);
    }, [isInView]);

    return (
        <div
            className={cn(
                'relative flex h-[420px] w-full max-w-[350px] flex-col items-start justify-between bg-light-grey p-[30px] opacity-0 shadow-[0px_4px_5px_rgba(0,0,0,0.25)] rounded-10 transition-all duration-1000 ease-in-out max-lg:max-w-none',
                isInView && 'opacity-100',
            )}
            ref={elementRef}
            onMouseEnter={() => lottieRef.current?.goToAndPlay(0, true)}
        >
            <div className="h-[120px] w-[86px] shrink-0 overflow-hidden">
                <Lottie
                    lottieRef={lottieRef}
                    animationData={certificateAnimation}
                    loop={false}
                    autoplay={false}
                    className="h-[120px] w-[86px]"
                />
            </div>
            <h3 className="w-full text-h4 font-medium leading-none text-dark-grey">
                {certificate.title}
            </h3>
            <p className="w-full text-sm font-medium leading-none text-dark-grey line-clamp-4">
                {certificate.description}
            </p>
            <a
                href={certificate.pdf}
                download
                className="group flex h-[47px] shrink-0 items-center gap-5 rounded-30 border border-dark-grey px-6 text-h5 font-medium text-dark-grey transition-colors duration-300 hover:border-transparent hover:bg-brand hover:text-white"
            >
                <span>{downloadLabel}</span>
                <span className="relative inline-flex h-[19px] w-[15px] shrink-0 items-center justify-center">
                    <Image
                        src="/img/icons/download-arrow-dark.svg"
                        alt=""
                        width={15}
                        height={19}
                        className="size-full group-hover:hidden"
                    />
                    <Image
                        src="/img/icons/download-arrow-light.svg"
                        alt=""
                        width={15}
                        height={19}
                        className="absolute inset-0 hidden size-full group-hover:block"
                    />
                </span>
            </a>
        </div>
    );
}
